import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  IconButton,
  Button,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import UniversalScoreBadge from './UniversalScoreBadge';
import TrustMicroline from './TrustMicroline';
import { trackClickApply } from './analytics';
import { getLogoPath } from './logoMap';

const LABELS = {
  es: { title: 'Comparación lado a lado', close: 'Cerrar comparación', apply: 'Aplicar' },
  en: { title: 'Side-by-side comparison', close: 'Close comparison', apply: 'Apply' },
  fr: { title: 'Comparaison côte à côte', close: 'Fermer la comparaison', apply: 'Appliquer' },
};

/**
 * Side-by-side comparison table built from config.comparisonRows.
 */
const keyRows = new Set(['monthly_fee', 'interest', 'bonus', 'newcomer', 'deposit_insurance', 'etransfer', 'cashback']);

const ProductComparisonModal = ({ open, onClose, items, config, category }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { i18n } = useTranslation();
  const l = LABELS[i18n.language] || LABELS.es;

  if (!items || items.length === 0) return null;

  const rows = config.comparisonRows || [];

  const handleCTAClick = (item, url) => {
    if (category && item.id) {
      trackClickApply(category, String(item.id), url);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={isMobile}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { borderRadius: isMobile ? 0 : '14px' },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          {l.title}
        </Typography>
        <IconButton onClick={onClose} size="small" aria-label={l.close}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 2, pb: 3, px: isMobile ? 2 : 3 }}>
        <Box
          className="mobile-scroll-container"
          sx={{ overflowX: 'auto', pb: 1 }}
        >
          <Box
            component="table"
            sx={{
              width: '100%',
              borderCollapse: 'collapse',
              '& th, & td': {
                px: { xs: 1, sm: 1.5 },
                py: 1.5,
                borderBottom: '1px solid',
                borderColor: 'divider',
                fontSize: { xs: '0.8125rem', sm: '0.875rem' },
                verticalAlign: 'top',
              },
              '& th': {
                fontWeight: 700,
                color: 'text.secondary',
                textAlign: 'left',
                whiteSpace: 'nowrap',
                minWidth: { xs: 80, sm: 120 },
              },
            }}
          >
            <thead>
              <tr>
                <th style={{ width: '22%', paddingBottom: '16px' }} />
                {items.map((item) => (
                  <th key={item.id} style={{ textAlign: 'center', paddingBottom: '16px' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                      {(() => {
                        const institution = item[config.institutionField] || '';
                        const name = item[config.nameField] || '';
                        const logoName = config.logoField ? (item[config.logoField] || '') : '';
                        const logo = getLogoPath(institution) || getLogoPath(name) || getLogoPath(logoName);
                        return logo ? (
                          <Box
                            sx={{
                              width: 56,
                              height: 32,
                              borderRadius: '8px',
                              bgcolor: 'neutral.50',
                              border: '1px solid',
                              borderColor: 'divider',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              overflow: 'hidden',
                              p: 0.5,
                              mb: 0.5,
                            }}
                          >
                            <Box
                              component="img"
                              src={logo}
                              alt={institution}
                              sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                            />
                          </Box>
                        ) : null;
                      })()}
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                        {item[config.institutionField]}
                      </Typography>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary', lineHeight: 1.2 }}>
                        {item[config.nameField]}
                      </Typography>
                      <UniversalScoreBadge item={item} />
                    </Box>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const isKey = keyRows.has(row.key);
                return (
                <tr key={row.key} style={isKey ? { backgroundColor: 'rgba(31,58,95,0.04)' } : undefined}>
                  <td style={isKey ? { borderLeft: '3px solid rgba(31,58,95,0.25)', paddingLeft: '10px' } : undefined}>
                    <Typography variant="body2" sx={{ fontWeight: isKey ? 700 : 600, color: isKey ? 'text.primary' : 'text.secondary' }}>
                      {row.label}
                    </Typography>
                  </td>
                  {items.map((item) => {
                    const value = row.render(item);
                    return (
                      <td key={item.id} style={{ textAlign: 'center' }}>
                        <Typography variant="body2" sx={{ fontWeight: isKey ? 600 : 500, color: isKey ? 'text.primary' : 'text.secondary' }}>
                          {value}
                        </Typography>
                      </td>
                    );
                  })}
                </tr>
                );
              })}
            </tbody>
          </Box>
        </Box>

        {/* CTAs row */}
        <Box sx={{ display: 'flex', gap: 1, mt: 3, justifyContent: 'center', flexWrap: 'wrap' }}>
          {items.map((item) => {
            const url = config.getApplyUrl?.(item);
            if (!url) return null;
            return (
              <Button
                key={item.id}
                variant="contained"
                size="small"
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => handleCTAClick(item, url)}
                sx={{
                  bgcolor: 'primary.main',
                  fontWeight: 600,
                  textTransform: 'none',
                  borderRadius: '8px',
                  '&:hover': { bgcolor: 'primary.dark' },
                }}
              >
                {isMobile ? l.apply : `${l.apply}: ${item[config.nameField]?.split(' ').slice(0, 2).join(' ')}`}
              </Button>
            );
          })}
        </Box>

        {/* Trust microline */}
        <TrustMicroline />
      </DialogContent>
    </Dialog>
  );
};

export default ProductComparisonModal;
